"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import type { FeatureFlag, FeatureFlagsState } from "@/shared/feature-flags";
import {
  DEFAULT_FEATURE_FLAGS,
  getDefaultFeatureFlag,
  isValidFeatureFlag
} from "@/shared/feature-flags";

interface FeatureFlagsContextType {
  flags: FeatureFlagsState;
  isEnabled: (flagId: FeatureFlag) => boolean;
  setFlag: (flagId: FeatureFlag, enabled: boolean) => void;
  resetToDefaults: () => void;
  resetFlag: (flagId: FeatureFlag) => void;
  forceResetToDefaults: () => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

export interface FeatureFlagsProviderProps {
  children: React.ReactNode;
  initialFlags?: FeatureFlagsState;
}

export function FeatureFlagsProvider({
  children,
  initialFlags
}: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<FeatureFlagsState>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("claimmosaic-feature-flags");
        if (saved) {
          const parsed = JSON.parse(saved);
          const validatedFlags: FeatureFlagsState = {};

          (Object.keys(DEFAULT_FEATURE_FLAGS) as FeatureFlag[]).forEach((key) => {
            if (isValidFeatureFlag(key) && typeof parsed[key] === "boolean") {
              validatedFlags[key] = parsed[key];
            } else {
              validatedFlags[key] = getDefaultFeatureFlag(key);
            }
          });

          // Keep tool traces visible by default after AI Elements migration.
          if (typeof validatedFlags["tool-call-details"] !== "boolean") {
            validatedFlags["tool-call-details"] = true;
          }

          return validatedFlags;
        }
      } catch (error) {
        console.warn("Failed to load feature flags from localStorage:", error);
      }
    }
    return initialFlags || DEFAULT_FEATURE_FLAGS;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(
          "claimmosaic-feature-flags",
          JSON.stringify(flags)
        );
      } catch (error) {
        console.warn("Failed to save feature flags to localStorage:", error);
      }
    }
  }, [flags]);

  const isEnabled = (flagId: FeatureFlag): boolean => {
    return flags[flagId] ?? getDefaultFeatureFlag(flagId);
  };

  const setFlag = (flagId: FeatureFlag, enabled: boolean) => {
    setFlags((prev) => ({
      ...prev,
      [flagId]: enabled
    }));
  };

  const resetToDefaults = () => {
    setFlags(DEFAULT_FEATURE_FLAGS);
  };

  const resetFlag = (flagId: FeatureFlag) => {
    setFlags((prev) => ({
      ...prev,
      [flagId]: getDefaultFeatureFlag(flagId)
    }));
  };

  const forceResetToDefaults = () => {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("claimmosaic-feature-flags");
      } catch (error) {
        console.warn("Failed to clear feature flags from localStorage:", error);
      }
    }
    setFlags(DEFAULT_FEATURE_FLAGS);
  };

  const value: FeatureFlagsContextType = {
    flags,
    isEnabled,
    setFlag,
    resetToDefaults,
    resetFlag,
    forceResetToDefaults
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider"
    );
  }

  return context;
}

export default useFeatureFlags;
